import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Row, Col, Typography, Rate, InputNumber, Button, Tag, Tabs, List, message, Image, Form, Input } from 'antd';
import { StarOutlined, SwapOutlined, EyeOutlined, HeartOutlined, MinusOutlined, PlusOutlined, CheckOutlined } from '@ant-design/icons';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { firestore } from 'config/firebase';

import DynamicBreadcrumb from 'components/DynamicBreadcrumb';
import { Link } from 'react-router-dom';
import OurServices from 'components/frontend/OurServices';
import { useCartContext } from 'contexts/CartContext';
import { useWishlistContext } from 'contexts/WishlistContext';

const { Title, Text, Paragraph } = Typography;

const ProductDetail = () => {
    const location = useLocation();
    const { addToCart } = useCartContext();
    const { addToWishlist, wishlistItems } = useWishlistContext();
    const [product, setProduct] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [selectedColor, setSelectedColor] = useState(null);
    const [selectedSize, setSelectedSize] = useState(null);
    const [mainImage, setMainImage] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProduct = async () => {
            const searchParams = new URLSearchParams(location.search);
            const slug = searchParams.get('category');

            if (slug) {
                try {
                    const productsRef = collection(firestore, 'products');
                    const q = query(productsRef, where("slug", "==", slug));
                    const querySnapshot = await getDocs(q);

                    if (!querySnapshot.empty) {
                        const productData = querySnapshot.docs[0].data();
                        setProduct(productData);
                        setMainImage(productData.images[0]);
                    } else {
                        message.error('Product not found');
                    }
                } catch (error) {
                    console.error('Error fetching product:', error);
                    message.error('Error fetching product');
                } finally {
                    setLoading(false);
                }
            }
        };

        fetchProduct();
    }, [location]);

    const handleQuantityChange = (value) => {
        setQuantity(value);
    };

    const handleColorSelect = (color) => {
        setSelectedColor(color);
    };

    const handleSizeSelect = (size) => {
        setSelectedSize(size);
    };

    const handleAddToCart = () => {
        if (!selectedColor || !selectedSize) {
            message.error('Please select both color and size');
            return;
        }

        addToCart({
            ...product,
            quantity,
            color: selectedColor,
            size: selectedSize
        });
        message.success('Added to cart successfully');
    };

    const handleAddToWishlist = () => {
        addToWishlist(product);
        message.success('Added to wishlist successfully');
    };

    const handleReviewSubmit = (values) => {
        console.log('Submitted review:', values);
        message.success('Thank you for your review!');
        // Here you would typically send this data to your backend
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    if (!product) {
        return <div>Product not found</div>;
    }

    const tabItems = [
        {
            key: '1',
            label: 'Description',
            children: (
                <>
                    <Title level={4}>Product Description</Title>
                    <Paragraph>{product.description}</Paragraph>
                    <Title level={5}>Features:</Title>
                    <List
                        dataSource={product.features}
                        renderItem={(item) => (
                            <List.Item>
                                <List.Item.Meta
                                    avatar={<CheckOutlined className='text-info' />}
                                    description={<Text>{item}</Text>}
                                />
                            </List.Item>
                        )}
                    />
                </>
            ),
        },
        {
            key: '2',
            label: 'Additional Information',
            children: (
                <>
                    <Title level={4}>Product Details</Title>
                    <List>
                        {product?.additionalInfo?.map((info, index) => (
                            <List.Item key={index}>
                                <List.Item.Meta
                                    title={info.title}
                                    description={info.description}
                                />
                            </List.Item>
                        ))}
                    </List>
                </>
            ),
        },
        {
            key: '3',
            label: 'Reviews',
            children: (
                <>
                    <Title level={4}>Customer Reviews</Title>
                    <List
                        itemLayout="horizontal"
                        dataSource={product.reviews}
                        renderItem={(item) => (
                            <List.Item>
                                <List.Item.Meta
                                    title={item.author}
                                    description={
                                        <>
                                            <Rate disabled defaultValue={item.rating} />
                                            <Paragraph>{item.content}</Paragraph>
                                        </>
                                    }
                                />
                            </List.Item>
                        )}
                    />
                    <Title level={4} className="mt-4">Write a Review</Title>
                    <Form layout="vertical" onFinish={handleReviewSubmit}>
                        <Form.Item name="rating" label="Rating">
                            <Rate />
                        </Form.Item>
                        <Form.Item name="name" label="Name">
                            <Input />
                        </Form.Item>
                        <Form.Item name="email" label="Email">
                            <Input />
                        </Form.Item>
                        <Form.Item name="review" label="Your Review" >
                            <Input.TextArea rows={4} style={{ resize: "none" }} />
                        </Form.Item>
                        <Form.Item>
                            <Button type="primary" size='large' htmlType="submit">
                                Submit
                            </Button>
                        </Form.Item>
                    </Form>
                </>
            ),
        }
    ];

    return (
        <div className="container mt-5">
            <DynamicBreadcrumb />
            <Row gutter={[32, 32]}>
                <Col xs={24} md={12}>
                    <Image src={mainImage} alt={product.name} className="mb-3 object-fit-contain " width={'100%'} height={450} />
                    <Row gutter={[16, 16]}>
                        {product.images.map((image, i) => (
                            <Col span={6} key={i}>
                                <Image preview={false} src={image} alt={`Thumbnail ${i + 1}`} className="img-fluid object-fit-contain mt-4" style={{ cursor: 'pointer' }} height={100} width={"100%"} onClick={() => setMainImage(image)} />
                            </Col>
                        ))}
                    </Row>
                </Col>
                <Col xs={24} md={12}>
                    <div className=" d-flex justify-content-between">
                        <Title level={2}>{product.brand}</Title>
                        <Tag color="success" className="mb-2" style={{ height: "25px" }}>In Stock</Tag>
                    </div>
                    <Title level={4}>{product.name}</Title>
                    <Rate allowHalf defaultValue={product.rating} disabled /> <Text>({product.reviewCount} Reviews)</Text>
                    <div className="mt-3 d-flex text-center">
                        <Title level={2} >${product.price.toFixed(2)}</Title>
                        {product.originalPrice && <Text delete className='ms-2 text-center align-self-center mb-2'>${product.originalPrice.toFixed(2)}</Text>}
                    </div>
                    <Text className="d-block mt-3">{product.shortDescription}</Text>
                    <div className="mt-4">
                        <Title level={5}>Color</Title>
                        <div>
                            {product?.colors?.map((color) => (
                                <Button key={color} shape="square" style={{ backgroundColor: color, marginRight: '8px', border: selectedColor === color ? '2px solid black' : 'none' }} onClick={() => handleColorSelect(color)} />
                            ))}
                        </div>
                    </div>
                    <div className="mt-4">
                        <Title level={5}>Size</Title>
                        <div>
                            {product?.sizes?.map((size) => (
                                <Button key={size} className="me-2" type={selectedSize === size ? 'primary' : 'default'} onClick={() => handleSizeSelect(size)}>
                                    {size}
                                </Button>
                            ))}
                        </div>
                    </div>
                    <div className="mt-4 d-flex align-items-center">
                        <div className="d-flex align-items-center border rounded">
                            <Button icon={<MinusOutlined />} onClick={() => handleQuantityChange(Math.max(1, quantity - 1))} disabled={quantity <= 1} className='border-0 antd-custom-buttom' />
                            <InputNumber min={1} value={quantity} onChange={handleQuantityChange} controls={false} className="mx-2 border-0" style={{ width: '50px', textAlign: 'center' }} />
                            <Button icon={<PlusOutlined />} onClick={() => handleQuantityChange(quantity + 1)} className='border-0 antd-custom-buttom' />
                        </div>
                        <Button type="primary" size="large" className="ms-3 flex-grow-1" onClick={handleAddToCart}>
                            Add to Cart
                        </Button>
                        <Button shape="circle" icon={<HeartOutlined />} size="large" className="ms-3" onClick={handleAddToWishlist} />
                    </div>
                </Col>
            </Row>
            <Row className="mt-5">
                <Col span={24}>
                    <Tabs defaultActiveKey="1" items={tabItems} />
                </Col>
            </Row>
            <OurServices />
        </div>
    );
};

export default ProductDetail;